import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/primitives/Button';
import {
  getNativeAccountCompletionPath,
  isAnonymousUser,
  NATIVE_ACCOUNT_COMPLETION_EVENT,
  readNativeAccountCompletion,
} from '@/utils/nativeAccountCompletion';

const HIDDEN_PATHS = new Set(['/', '/auth', '/onboarding', '/complete-account']);

export function NativeAccountReminder() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [revision, setRevision] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const refresh = () => setRevision((value) => value + 1);
    window.addEventListener(NATIVE_ACCOUNT_COMPLETION_EVENT, refresh);
    return () => window.removeEventListener(NATIVE_ACCOUNT_COMPLETION_EVENT, refresh);
  }, []);

  void revision;
  const state = readNativeAccountCompletion(user?.id);
  if (
    !Capacitor.isNativePlatform() ||
    !isAnonymousUser(user) ||
    state?.reason !== 'purchase' ||
    state.status !== 'deferred' ||
    HIDDEN_PATHS.has(location.pathname) ||
    isDismissed
  ) {
    return null;
  }

  return (
    <aside
      className="fixed inset-x-3 z-[70] mx-auto flex max-w-lg items-center gap-3 rounded-2xl border border-jp-accent-secondary/35 bg-jp-bg-card/95 p-3 shadow-[0_18px_55px_-20px_rgba(1,4,9,0.95)] backdrop-blur"
      style={{
        bottom: 'max(5.5rem, calc(env(safe-area-inset-bottom) + 5rem))',
      }}
      aria-label="Connect your membership"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-jp-accent-secondary/12 text-jp-accent-secondary">
        <Mail className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-v7-body text-sm font-bold text-jp-fg-primary">
          Connect your membership
        </p>
        <p className="mt-0.5 font-v7-body text-xs leading-5 text-jp-fg-secondary">
          Connect it to use it on the web or another device.
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={() =>
          navigate(getNativeAccountCompletionPath(state.reason, state.returnPath))
        }
      >
        Connect
      </Button>
      <button
        type="button"
        aria-label="Dismiss reminder"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-jp-fg-faint hover:bg-jp-bg-utility hover:text-jp-fg-primary"
        onClick={() => setIsDismissed(true)}
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </aside>
  );
}
